// LAP-733 (GE022) — Notifications settings panel.
//
// - All static labels go through react-i18next (namespace: layout).
// - Section headers + per-row descriptions come from the backend, which now
//   ships translations via TranslatableSerializerMixin (user app i18n).
// - Design refresh: navy gradient header, white cards, accent-blue CTA. The
//   old cyan accordion summary (#5bdfeb — "très moche") is gone.
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { makePostRequest, makeGetRequest } from 'utils/api'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'

const NAVY = '#001B42'
const ACCENT = '#2563EB'
const SLATE = '#475569'
const SLATE_BORDER = '#E5E7EB'

export default function Notification({ appUser, setAppUser, handleClose, fetchUser }) {
  const { t } = useTranslation('layout')

  const [usernpList, setUserNPList] = useState([])

  useEffect(() => {
    getUserNotificationParameter()
  }, [])

  const getUserNotificationParameter = async () => {
    const response = await makeGetRequest('/api/user/user-notification-setting/', {}, true)
    if (response.status === 200) {
      setUserNPList(response.data)
    }
  }
  const { handleSubmit } = useForm({ defaultValues: {} })

  const onSubmit = async () => {
    let list = []
    usernpList.forEach((el) => {
      el.list.forEach((e) => list.push({ id: e.id, value: e.value, active: e.active }))
    })
    const payload = { notification: 'update', data: list }
    const response = await makePostRequest(
      '/api/user/update-user-notification/',
      payload,
      {},
      true,
    )
    if (response.status === 200) {
      alert(t('settings.notifications.saved'))
    }
  }
  const handleInput = (e, index, nfindex) => {
    const unf = [...usernpList]
    unf[index].list[nfindex].value = e.target.value
    setUserNPList(unf)
  }
  const handleCheck = (e, index, nfindex) => {
    const unf = [...usernpList]
    unf[index].list[nfindex].active = !unf[index].list[nfindex].active
    setUserNPList(unf)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: '#F8FAFC',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #0A2A66 100%)`,
            color: 'white',
            px: { xs: 2, md: 4 },
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <NotificationsActiveOutlinedIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
              {t('settings.notifications.title')}
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
              {t('settings.notifications.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Sections */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: { xs: 2, md: 4 },
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          {usernpList.map((section, index) => (
            <Card
              key={section.name || index}
              variant="outlined"
              sx={{
                borderColor: SLATE_BORDER,
                borderRadius: 2,
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 1.75,
                  borderBottom: `1px solid ${SLATE_BORDER}`,
                  bgcolor: '#F8FAFC',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: NAVY,
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                  }}
                >
                  {section.name}
                </Typography>
              </Box>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {section.list.map((nf, nfindex) => (
                  <Box
                    key={nf.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 3,
                      py: 1.5,
                      borderBottom:
                        nfindex === section.list.length - 1
                          ? 'none'
                          : `1px solid ${SLATE_BORDER}`,
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 14, color: '#0F172A' }}>
                        {nf.settings_descripton}
                      </Typography>
                      {nf.helptext && (
                        <Tooltip title={nf.helptext} placement="top" arrow>
                          <IconButton size="small" sx={{ color: SLATE }}>
                            <InfoOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ width: 160, display: 'flex', justifyContent: 'flex-end' }}>
                      {nf.type === 'Value' ? (
                        <TextField
                          size="small"
                          type="number"
                          value={nf.value ?? ''}
                          onChange={(e) => handleInput(e, index, nfindex)}
                          sx={{ width: 140 }}
                          InputProps={{
                            endAdornment: nf.symbol ? (
                              <InputAdornment position="end">{nf.symbol}</InputAdornment>
                            ) : null,
                          }}
                        />
                      ) : (
                        <Checkbox
                          checked={!!nf.active}
                          onChange={(e) => handleCheck(e, index, nfindex)}
                          sx={{
                            color: SLATE,
                            '&.Mui-checked': { color: ACCENT },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
                {section.list.length === 0 && (
                  <Box sx={{ px: 3, py: 3, textAlign: 'center', color: SLATE, fontSize: 13 }}>
                    {t('settings.notifications.subtitle')}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            bgcolor: 'white',
            px: { xs: 2, md: 4 },
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                color: SLATE,
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
            >
              {t('settings.notifications.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                bgcolor: ACCENT,
                px: 3,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' },
              }}
            >
              {t('settings.notifications.save')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </form>
  )
}
